import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

import { setFundChartSecurityGroup } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/security-group/security-group.actions';
import { setFundChartSecuritylistName } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securitylist-name/security-list-name.actions';
import { setFundChartSecuritylistSecurityId } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securitylist-security-id/security-list-security-id.actions';
import { setTechChartSecurityGroup } from 'ChartApp/ts/features/tech-chart-page-shared/state/reducers/security-group/security-group.actions';
import { setTechChartSecuritylistName } from 'ChartApp/ts/features/tech-chart-page-shared/state/reducers/securitylist-name/security-list-name.actions';
import { setTechChartSecuritylistSecurityId } from 'ChartApp/ts/features/tech-chart-page-shared/state/reducers/securitylist-security-id/securitylist-security-id.actions';
import { addSecuritiesInfo } from 'SecuritiesApp/ts/state/securities-info/securities-info.actions';
import {
    getAppStateSliceKey,
    getSecurityGroup,
    getSecuritylistName,
    getSecuritylistSecurityId,
    getSelectedSecuritylistItemDisplayId,
} from 'ChartApp/ts/state/base-chart.selectors';

import {
    ALTERNATIVE,
    BENCHMARK_PORTFOLIO,
    BOND,
    CEF,
    CLIENT_PORTFOLIO,
    CUSTOM_SECURITY,
    ETF,
    GSAC_BOND,
    HOUSEHOLD_PORTFOLIO,
    INDEX,
    INDICATOR,
    MODEL_PORTFOLIO,
    MONEY_MARKET_FUND,
    MUNICIPAL_BOND,
    MUTUAL_FUND,
    REGISTRATION_PORTFOLIO,
    SEPARATE_ACCOUNT,
    STOCK,
    UIT,
    UNLISTED_CEF,
} from 'Angular/constants/core.constants';
import { FUND_CHART_APP_STATE_SLICE_KEY } from 'ChartApp/ts/features/fundamental-chart/constants';
import { TECH_CHART_APP_STATE_SLICE_KEY } from 'ChartApp/ts/features/tech-chart/constants';
import { SecurityGroupName } from 'SecurityListsApp/ts/features/security-list-selector-modals/components/security-list-group-tabs/types/security-group-name.type';
import { ChartRootStore } from 'ChartApp/ts/interfaces/chart-common.interface';
import { SecurityListLabels } from 'SecurityListsApp/ts/interfaces/security-list-labels.interface';

import { SecuritiesService } from 'SecuritiesApp/ts/services/securities.service';
import { SecurityListService } from 'SecurityListsApp/ts/services/security-list.service';
import { SecurityListMenuService } from 'SecurityListsApp/ts/services/security-list-menu.service';

const VALID_DETAILED_SECURITY_TYPES = [
    ALTERNATIVE,
    BENCHMARK_PORTFOLIO,
    BOND,
    CEF,
    CLIENT_PORTFOLIO,
    CUSTOM_SECURITY,
    ETF,
    GSAC_BOND,
    HOUSEHOLD_PORTFOLIO,
    INDEX,
    INDICATOR,
    MODEL_PORTFOLIO,
    MONEY_MARKET_FUND,
    MUNICIPAL_BOND,
    MUTUAL_FUND,
    REGISTRATION_PORTFOLIO,
    SEPARATE_ACCOUNT,
    STOCK,
    UIT,
    UNLISTED_CEF,
];

@Component({
    standalone: false,
    selector: 'ycn-chart-base-list-browser',
    templateUrl: './chart-base-list-browser.template.html',
})
export class ChartBaseListBrowserComponent implements OnInit, OnDestroy {
    securitylistMenuLabelMap!: SecurityListLabels;

    displaySecurityList: string | undefined;

    displaySecurityId: string = '';

    securitylistName: string = '';

    securitylistSecurityId: string = '';

    securitylistSecurityIds: string[] = [];

    selectedIndex = 0;

    isAlive = true;

    securityInfoFetchSubscription: Subscription | null = null;

    appStateSliceKey!: typeof TECH_CHART_APP_STATE_SLICE_KEY | typeof FUND_CHART_APP_STATE_SLICE_KEY;

    constructor(
        private securitiesService: SecuritiesService,
        private securityListMenuService: SecurityListMenuService,
        private securityListService: SecurityListService,
        private readonly store: Store<{ reducers: ChartRootStore }>
    ) {
        this.store
            .select('reducers')
            .pipe(takeWhile(() => this.isAlive))
            .subscribe(this.mapStateToComponent);
    }

    ngOnInit(): void {
        this.securityListMenuService
            .getLabels()
            .pipe(takeWhile(() => this.isAlive))
            .subscribe((securitylistMenuLabelMap) => {
                this.securitylistMenuLabelMap = securitylistMenuLabelMap;
            });
    }

    ngOnDestroy(): void {
        this.isAlive = false;
        if (this.securityInfoFetchSubscription !== null) {
            this.securityInfoFetchSubscription.unsubscribe();
        }
    }

    @HostListener('document:keydown.ArrowLeft', ['$event'])
    @HostListener('document:keydown.ArrowRight', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        // If the element is an input element then ignore it because
        // we don't want to respond to left and right arrows within
        // a text input box.
        if (ChartBaseListBrowserComponent.isInputElement(event.target as HTMLElement)) {
            return;
        }

        if (event.code === 'ArrowLeft') {
            this.prevHandler();
        } else if (event.code === 'ArrowRight') {
            this.nextHandler();
        }
    }

    /**
     * Check if the given element is an input element.
     *
     * @private
     * @param element
     * @type HTMLElement
     *
     * @return boolean
     */
    private static isInputElement(element: HTMLElement): boolean {
        return element && element.tagName === 'INPUT';
    }

    mapStateToComponent = (state: ChartRootStore): void => {
        this.appStateSliceKey = getAppStateSliceKey(state);
        const { securitiesInfo } = state;
        const securityGroup = getSecurityGroup(state) as SecurityGroupName;
        this.displaySecurityId = getSelectedSecuritylistItemDisplayId(state);
        const prevSecuritylistSecurityId = this.securitylistSecurityId;
        this.securitylistSecurityId = getSecuritylistSecurityId(state);
        const prevSecuritylistName = this.securitylistName;
        this.securitylistName = getSecuritylistName(state);
        if (!this.securitylistName) {
            this.securitylistSecurityIds = [];
        }

        if (this.securitylistMenuLabelMap?.[securityGroup]) {
            this.displaySecurityList = this.securitylistMenuLabelMap[securityGroup][this.securitylistName];
        } else {
            this.displaySecurityList = '';
        }

        if (this.securitylistName && prevSecuritylistName !== this.securitylistName) {
            this.securityListService
                .getMembers(this.securitylistName, securityGroup, VALID_DETAILED_SECURITY_TYPES)
                .subscribe((data) => {
                    this.securitylistSecurityIds = data.map((s) => s.security_id);
                    let newSecuritylistSecurityId = this.securitylistSecurityId;
                    if (data.length > 0) {
                        const validNewSecuritylistSecurityId = data.find((security) => {
                            return security.security_id === newSecuritylistSecurityId;
                        });
                        if (prevSecuritylistSecurityId || !validNewSecuritylistSecurityId) {
                            newSecuritylistSecurityId = data[0].security_id;
                        }
                        if (this.appStateSliceKey === FUND_CHART_APP_STATE_SLICE_KEY) {
                            this.store.dispatch(
                                setFundChartSecuritylistSecurityId({
                                    securitylistSecurityId: newSecuritylistSecurityId,
                                })
                            );
                        } else if (this.appStateSliceKey === TECH_CHART_APP_STATE_SLICE_KEY) {
                            this.store.dispatch(
                                setTechChartSecuritylistSecurityId({
                                    securitylistSecurityId: newSecuritylistSecurityId,
                                })
                            );
                        }
                    }
                    this.selectedIndex = this.securitylistSecurityIds.indexOf(newSecuritylistSecurityId);
                });
        }

        if (this.securitylistSecurityId && !securitiesInfo[this.securitylistSecurityId]) {
            if (this.securityInfoFetchSubscription !== null) {
                this.securityInfoFetchSubscription.unsubscribe();
            }
            this.securityInfoFetchSubscription = this.securitiesService
                .getSecuritiesInfo(this.securitylistSecurityId)
                .subscribe((_data) => {
                    const validSecurityId = _data.find((security) => {
                        return security.id === this.securitylistSecurityId;
                    });

                    // if security is missing from the response, it does not exist
                    // or the user does not have access to it
                    if (!validSecurityId) {
                        this.nextHandler();
                    }
                    this.store.dispatch(addSecuritiesInfo({ securitiesInfo: _data }));
                });
        }
    };

    removeSecuritylist(): void {
        if (this.appStateSliceKey === FUND_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setFundChartSecuritylistName({ securitylistName: '' }));
            this.store.dispatch(setFundChartSecurityGroup({ securityGroup: '' }));
            this.store.dispatch(setFundChartSecuritylistSecurityId({ securitylistSecurityId: '' }));
        } else if (this.appStateSliceKey === TECH_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setTechChartSecuritylistName({ securitylistName: '' }));
            this.store.dispatch(setTechChartSecurityGroup({ securityGroup: '' }));
            this.store.dispatch(setTechChartSecuritylistSecurityId({ securitylistSecurityId: '' }));
        }
    }

    prevHandler(): void {
        if (this.securitylistSecurityIds.length === 0) {
            return;
        }

        if (this.selectedIndex === 0) {
            this.selectedIndex = this.securitylistSecurityIds.length - 1;
        } else {
            this.selectedIndex -= 1;
        }

        const securityId = this.securitylistSecurityIds[this.selectedIndex];
        if (this.appStateSliceKey === FUND_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setFundChartSecuritylistSecurityId({ securitylistSecurityId: securityId }));
        } else if (this.appStateSliceKey === TECH_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setTechChartSecuritylistSecurityId({ securitylistSecurityId: securityId }));
        }
    }

    nextHandler(): void {
        if (this.securitylistSecurityIds.length === 0) {
            return;
        }

        if (this.selectedIndex === this.securitylistSecurityIds.length - 1) {
            this.selectedIndex = 0;
        } else {
            this.selectedIndex += 1;
        }

        const securityId = this.securitylistSecurityIds[this.selectedIndex];
        if (this.appStateSliceKey === FUND_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setFundChartSecuritylistSecurityId({ securitylistSecurityId: securityId }));
        } else if (this.appStateSliceKey === TECH_CHART_APP_STATE_SLICE_KEY) {
            this.store.dispatch(setTechChartSecuritylistSecurityId({ securitylistSecurityId: securityId }));
        }
    }
}
